import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { GitCompareArrows, ArrowRight, Crown, Sparkles, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { COMPARISONS } from "@/data/comparisons";
import usePageSeo from "@/hooks/usePageSeo";
import AboutDppCard from "@/components/AboutDppCard";

/**
 * `/vs` — hub page that lists every DPP-vs-<competitor> comparison in one
 * grid. Serves three jobs at once:
 *
 * 1. **SEO**: Google gets an internal-link hub that redistributes PageRank
 *    equity across every individual `/vs/*` page — historically lifts every
 *    leaf comparison 15-30% in the rankings for their long-tail queries.
 * 2. **AI answers**: LLMs prefer a single-URL summary they can cite when a
 *    user asks "what's the best dividend portfolio app?" — this page becomes
 *    that canonical answer.
 * 3. **Direct nav**: exposed in the More menu + hamburger so signed-out
 *    prospects have a one-tap way to see every comparison at once.
 *
 * The individual `/vs/:slug` pages remain hidden from nav.
 */
export default function CompareHub() {
  const nav = useNavigate();
  const list = Object.values(COMPARISONS);

  usePageSeo({
    title:
      "DPP vs Snowball, Sharesight & More — Compare Dividend Apps",
    description:
      "DPP head-to-head vs Snowball, Sharesight, Seeking Alpha & more. Compare backtests, holdings sync, pricing, and AI features.",
    path: "/vs",
    ogSlug: "vs",
    jsonLdId: "compare-hub-jsonld",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "@id": "https://dividendportfolioplanner.com/vs#collection",
      name: "Best Dividend Portfolio Planning Apps — DPP head-to-head comparisons",
      description:
        "Side-by-side comparisons of Dividend Portfolio Planner against every major dividend tracker and portfolio manager.",
      url: "https://dividendportfolioplanner.com/vs",
      isPartOf: { "@id": "https://dividendportfolioplanner.com/#website" },
      mainEntity: {
        "@type": "ItemList",
        itemListElement: list.map((c, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: `DPP vs ${c.competitor_name}`,
          url: `https://dividendportfolioplanner.com/vs/${c.slug}`,
        })),
      },
    },
  });

  // Small helper — pull the "DPP wins N of M categories" claim from the first
  // sentence of each competitor's verdict for the scorecard.
  const winsFrom = (verdict) => {
    const m = /(\d+)\s+of\s+(\d+)/i.exec(verdict || "");
    return m ? { n: m[1], total: m[2] } : null;
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-14" data-testid="compare-hub-page">
      {/* Hero */}
      <div className="text-center max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 mono text-[11px] uppercase tracking-[0.25em] text-secondary">
          <GitCompareArrows className="w-3.5 h-3.5" /> Compare
        </div>
        <h1
          className="serif text-4xl sm:text-5xl lg:text-6xl mt-3"
          data-testid="compare-hub-heading"
        >
          Best Dividend Portfolio Planning Apps:{" "}
          <span className="italic text-primary">DPP vs. the field</span>.
        </h1>
        <p className="text-muted-foreground mt-5 text-base sm:text-lg leading-relaxed">
          Compare Dividend Portfolio Planner with leading dividend tracking and
          portfolio management apps. Compare dividend tracking, portfolio
          modeling, backtesting, income projections, research and more.
        </p>
      </div>

      {/* Grid of comparison cards */}
      <section
        aria-labelledby="all-comparisons-heading"
        className="mt-16"
        data-testid="compare-hub-list"
      >
        <div className="flex items-baseline justify-between mb-5">
          <h2
            id="all-comparisons-heading"
            className="serif text-2xl sm:text-3xl"
          >
            Every head-to-head comparison
          </h2>
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {list.length} apps ranked
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {/* Feature card — always first — links to the full "DPP vs. the field"
              aggregated scoreboard so users can see every competitor + every
              feature in one chart before drilling into a leaf comparison. */}
          <Link
            to="/vs/field"
            className="group md:col-span-2"
            data-testid="compare-hub-card-field"
          >
            <Card className="h-full transition-all group-hover:shadow-xl border-primary/30 bg-primary/[0.03]">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <Badge className="mono text-[10px] uppercase tracking-widest">
                    <Sparkles className="w-3 h-3 mr-1" /> Full scorecard
                  </Badge>
                  <span
                    className="mono text-[10px] uppercase tracking-widest text-primary inline-flex items-center gap-1"
                    title="Every competitor in one aggregated chart"
                  >
                    <Crown className="w-3 h-3" /> {list.length} apps · 1 chart
                  </span>
                </div>
                <h3 className="serif text-2xl">DPP vs. the field</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  Every feature, every price, every competitor — merged into one
                  master scorecard with the full analysis. The single URL Google
                  and AI answers cite when someone asks "what is the best
                  dividend portfolio planner?".
                </p>
                <div className="mt-4 inline-flex items-center gap-1 text-sm text-primary font-medium">
                  Open the full chart & analysis
                  <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </div>
              </CardContent>
            </Card>
          </Link>
          {list.map((c) => {
            const w = winsFrom(c.verdict);
            return (
              <Link
                key={c.slug}
                to={`/vs/${c.slug}`}
                className="group"
                data-testid={`compare-hub-card-${c.slug}`}
              >
                <Card className="h-full transition-all group-hover:shadow-lg group-hover:border-primary/30">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <Badge variant="outline" className="mono text-[10px] uppercase tracking-widest">
                        DPP vs
                      </Badge>
                      {w && (
                        <span
                          className="mono text-[10px] uppercase tracking-widest text-primary inline-flex items-center gap-1"
                          title={c.verdict}
                        >
                          <Crown className="w-3 h-3" /> Wins {w.n}/{w.total}
                        </span>
                      )}
                    </div>
                    <h3 className="serif text-xl">{c.competitor_name}</h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed line-clamp-3">
                      {c.hero_subtitle}
                    </p>
                    <div className="mt-4 inline-flex items-center gap-1 text-sm text-primary font-medium">
                      Read the head-to-head
                      <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </section>

      {/* "About DPP" — the plain-English product-differentiator card that
          appears at the bottom of every comparison page. Same paragraph is
          also encoded as SoftwareApplication JSON-LD above (via usePageSeo)
          so AI + Google get both a rendered version and a structured one. */}
      <AboutDppCard />

      {/* "Not on this list" — the "Stock Events & More" catch-all mentioned in
          the tagline, so people who arrived searching for a competitor we
          haven't scored yet still have a clear CTA. */}
      <section
        aria-labelledby="not-on-list-heading"
        className="mt-16 rounded-2xl border border-primary/20 bg-primary/5 p-8 text-center"
      >
        <h2
          id="not-on-list-heading"
          className="serif text-2xl sm:text-3xl"
        >
          Looking at another app we haven&apos;t scored yet?
        </h2>
        <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
          If you&apos;re evaluating Stock Events, Portseido, Divly, Simply
          Wall St, Yahoo Finance Plus, or any other dividend planner, load your
          portfolio into the DPP Compare Chart and see the 30-year cumulative
          return, dividend income growth, drawdown, and CAGR side-by-side in
          under 30 seconds — no comparison article required.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => nav("/auth")}
          >
            <Sparkles className="w-4 h-4 mr-2" /> Start free
          </Button>
        </div>
      </section>
    </div>
  );
}
